import { Icon } from '@zapier/design-system';
import { Button } from '@zapier/design-system-beta';
import { clsx } from 'clsx';
import React, { forwardRef, useRef, useImperativeHandle, useEffect, useCallback } from 'react';
import { CopilotMessage } from '../CopilotMessage/CopilotMessage.es.js';

const messageList = "_messageList_tpzsu_1";
const hideScrollbar = "_hideScrollbar_tpzsu_8";
const thinkingIndicator = "_thinkingIndicator_tpzsu_16";
const pulse = "_pulse_tpzsu_1";
const errorContainer = "_errorContainer_tpzsu_34";
const errorIcon = "_errorIcon_tpzsu_44";
const errorContent = "_errorContent_tpzsu_53";
const errorHeader = "_errorHeader_tpzsu_60";
const errorMessage = "_errorMessage_tpzsu_65";
const errorActions = "_errorActions_tpzsu_70";
const styles = {
	messageList: messageList,
	hideScrollbar: hideScrollbar,
	thinkingIndicator: thinkingIndicator,
	pulse: pulse,
	errorContainer: errorContainer,
	errorIcon: errorIcon,
	errorContent: errorContent,
	errorHeader: errorHeader,
	errorMessage: errorMessage,
	errorActions: errorActions
};

const CopilotMessageList = forwardRef((props, ref) => {
  const { messages, className, style, renderMessage, autoScroll = true, renderPart, isDebug, renderPartContent, getToolCallDisplay, actions, status, error } = props;
  const innerRef = useRef(null);
  useImperativeHandle(ref, () => innerRef.current, []);
  const isUserAtBottomRef = useRef(true);
  useEffect(() => {
    if (!autoScroll)
      return;
    const container = innerRef.current;
    if (!container)
      return;
    const handleScroll = () => {
      const threshold = 8;
      const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight <= threshold;
      isUserAtBottomRef.current = isAtBottom;
      if (isAtBottom) {
        container.classList.add(styles.hideScrollbar);
      } else {
        container.classList.remove(styles.hideScrollbar);
      }
    };
    container.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [autoScroll]);
  useEffect(() => {
    if (!autoScroll)
      return;
    const container = innerRef.current;
    if (!container)
      return;
    container.scrollTop = container.scrollHeight;
    isUserAtBottomRef.current = true;
    container.classList.add(styles.hideScrollbar);
  }, [autoScroll]);
  useEffect(() => {
    if (!autoScroll)
      return;
    const container = innerRef.current;
    if (!container)
      return;
    if (isUserAtBottomRef.current) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages, autoScroll]);
  const renderSingleMessage = useCallback((message, index) => {
    const customRendered = renderMessage ? renderMessage(message, index) : void 0;
    return React.createElement(React.Fragment, { key: message.id }, customRendered !== void 0 ? customRendered : React.createElement(CopilotMessage, { message, renderPart, isDebug, renderPartContent, getToolCallDisplay, actions }));
  }, [
    renderMessage,
    renderPart,
    isDebug,
    renderPartContent,
    getToolCallDisplay,
    actions
  ]);
  return React.createElement(
    "div",
    { ref: innerRef, className: clsx(styles.messageList, className, {
      [styles.hideScrollbar]: isUserAtBottomRef.current
    }), style, "data-zds": true },
    messages.map(renderSingleMessage),
    status === "submitted" && React.createElement("div", { className: styles.thinkingIndicator, "data-zds": true }, "Thinking...".split("").map((char, i) => React.createElement("span", { key: i, style: { animationDelay: `${i * 0.1}s` } }, char))),
    error && React.createElement(
      "div",
      { className: styles.errorContainer, "data-zds": true },
      React.createElement(
        "div",
        { className: styles.errorIcon },
        React.createElement(Icon, { name: "formXCircle", size: "small" })
      ),
      React.createElement(
        "div",
        { className: styles.errorContent },
        React.createElement("div", { className: styles.errorHeader }, error.header || "Copilot isn't responding right now"),
        React.createElement("div", { className: styles.errorMessage }, error.message),
        error.onRetry && React.createElement(
          "div",
          { className: styles.errorActions },
          React.createElement(Button, { onPress: error.onRetry, size: "small", variant: "primary" }, error.retryText || "Retry")
        )
      )
    )
  );
});
CopilotMessageList.displayName = "CopilotMessageList";

export { CopilotMessageList, CopilotMessageList as default };
//# sourceMappingURL=CopilotMessageList.es.js.map
